import { hTabKey } from "@/components/HorizontalTabs";
import type { TNotificationRoute } from "@/types/notifications.interface";
import {
  FileText,
  FileCheck,
  FileX,
  FileClock,
  ClipboardCheck,
  Clock,
  Trash2,
  CalendarDays,
  ClipboardX,
  AlarmClock,
  BellRing,
  AlertCircle,
  type LucideIcon,
} from "lucide-react";

const defaultAudio = new Audio("/audio/notification.mp3");

export const notificationIconMap: Record<string, LucideIcon> = {
  FileText,
  FileCheck,
  FileX,
  FileClock,
  CalendarDays,
  ClipboardCheck,
  ClipboardX,
  Clock,
  Trash2,
  AlarmClock,
  BellRing,
  AlertCircle,
};

export const formatNotificaitonTime = (date: string | Date | number) => {
  if (!date) return "";

  const d =
    typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;

  if (isNaN(d.getTime())) return "";

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000);

  if (diffInSeconds < 60) return "Just now";

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes} min ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24)
    return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30)
    return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12)
    return `${diffInMonths} month${diffInMonths > 1 ? "s" : ""} ago`;

  const diffInYears = Math.floor(diffInMonths / 12);
  return `${diffInYears} year${diffInYears > 1 ? "s" : ""} ago`;
};

export const playNotificationSound = () => {
  try {
    defaultAudio.currentTime = 0;
    // play() rejects (it doesn't throw) when the browser blocks autoplay —
    // before the first click on the page. Catch it, or it's an unhandled rejection.
    void defaultAudio.play().catch((err) => console.warn("Audio play blocked:", err));
  } catch (err) {
    console.warn("Audio play blocked:", err);
  }
};

export const speak = (
  text: string,
  options?: {
    rate?: number;
    pitch?: number;
    volume?: number;
    lang?: string;
    /** Fires when the utterance finishes, is cancelled, or errors. */
    onEnd?: () => void;
  },
) => {
  if (!window.speechSynthesis) {
    console.warn("Speech synthesis not supported");
    return;
  }

  window.speechSynthesis.cancel();

  const utterance = new SpeechSynthesisUtterance(text);
  // cancel() on the next speak() fires either 'end' or 'error' depending on the
  // browser — wire both so callers reliably learn playback stopped.
  if (options?.onEnd) {
    utterance.onend = options.onEnd;
    utterance.onerror = options.onEnd;
  }

  // Find Samantha voice, fallback to first english if not available
  const voices = window.speechSynthesis.getVoices();
  const samantha =
    voices.find((v) => v.name === "Samantha") ||
    voices.find((v) => v.name === "Google US English Female") ||
    voices.find((v) => v.lang.startsWith("en")) ||
    null;

  if (samantha) utterance.voice = samantha;

  utterance.rate = options?.rate ?? 1;
  utterance.pitch = options?.pitch ?? 1;
  utterance.volume = options?.volume ?? 1;
  utterance.lang = options?.lang ?? "en-US";

  // Chrome loads voices async - handle both cases
  if (voices.length > 0) {
    window.speechSynthesis.speak(utterance);
  } else {
    window.speechSynthesis.onvoiceschanged = () => {
      const v = window.speechSynthesis.getVoices();
      utterance.voice =
        v.find((v) => v.name === "Samantha") ||
        v.find((v) => v.name === "Google US English") ||
        v.find((v) => v.lang.startsWith("en")) ||
        v[0];
      window.speechSynthesis.speak(utterance);
      window.speechSynthesis.onvoiceschanged = null;
    };
  }
};

// Convert a markdown string into plain prose suitable for text-to-speech.
// Drops code blocks, link URLs, and markdown syntax so the spoken output
// reads naturally instead of announcing asterisks, backticks, and URLs.
export const stripMarkdownToText = (markdown: string): string =>
  markdown
    // fenced code blocks — don't read code aloud
    .replace(/```[\s\S]*?```/g, " ")
    // inline code — keep the inner text, drop the backticks
    .replace(/`([^`]+)`/g, "$1")
    // images — drop entirely
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    // links — keep the label, drop the URL
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    // headings, blockquotes and list markers at the start of a line
    .replace(/^\s{0,3}(?:#{1,6}|>|[-*+]|\d+\.)\s+/gm, "")
    // emphasis / strikethrough markers
    .replace(/(\*\*|__|\*|_|~~)/g, "")
    // table separators and pipes
    .replace(/^\s*:?-{2,}:?\s*$/gm, " ")
    .replace(/\|/g, " ")
    // stray html
    .replace(/<[^>]*>/g, "")
    // tidy whitespace
    .replace(/\n{2,}/g, ". ")
    .replace(/\s{2,}/g, " ")
    .trim();

export const buildNotificationUrl = (
  route: TNotificationRoute,
  id?: string | number,
): string => {
  const basePath =
    typeof route.path === "function" ? route.path(id!) : route.path;

  const params = new URLSearchParams();
  if (route.tab) params.set(hTabKey, route.tab);
  if (route.paramKey && id !== undefined)
    params.set(route.paramKey, String(id));
  for (const [key, value] of Object.entries(route.query ?? {}))
    params.set(key, value);
  const queryString = params.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
};
